import { ISectionLogger } from "@multiweb/logging";
import { OWDataType } from "@multiweb/types/contracts";
import { ITextBuilder } from "../../../../Global/Base/Builders/ITextBuilder";
import { Cursor } from "../../../../Global/Base/Enums";
import { Event } from "../../../../Global/Base/Events";
import { IDisposable } from "../../../../Global/Base/IDisposable";
import { IItemRightsCollection } from "../../../../Global/Base/Rights/RightTypes";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { IInteractive } from "../../../CommonUIInterfaces";
import { IEnabledVisualControl, IValidatable } from "../../../UI.BaseControls/ControlInterfaces";
import { FrameContext } from "../UIManagerInterfaces";
import { IControlInfoObject } from "../XGControls/ControlInfo";
import { FilterPanel } from "../XGControls/Filters/FilterPanel";
import { IForm } from "../XGControls/IForm";
/** Reexport striktních typů z ControlInterfacesStrict kvůli zpětné kompatibilitě. */
import {
    IControl,
    IEnable,
    IGenericControl,
    IHasCulture,
    IHasEncryptionState,
    ILoadableValue,
    ILocalizable,
    IVisible,
    IWithOriginalRightsDefinition,
} from "./ControlInterfacesStrict";

export {
    IControl,
    IEnable,
    IGenericControl,
    IHasCulture,
    IHasEncryptionState,
    ILoadableValue,
    ILocalizable,
    IVisible,
    IWithOriginalRightsDefinition,
};

/**
 * HTML element, který má pro svůj provoz alokované další zdroje. Proto obsahuje rozhraní {@link IDisposable} k jejich uvolnění.
 */
export interface IHtmlElementWithResources extends HTMLElement, IDisposable {}

export class IHtmlElementWithResources {
    private static _interfaces = [IDisposable];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && this._interfaces.every((_interface) => instance instanceof _interface);
    }
}

export interface IHTMLElementWithControl extends HTMLElement {
    tscControl: IGenericControl;
}

export class IHTMLElementWithControl {
    private static _properties = ["tscControl"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && this._properties.every((value) => Reflect.has(instance, value)) && instance instanceof HTMLElement;
    }
}

export interface ICompositeComponent {
    /**
     * Příznak, jestli se jedná o kompozitní komponentu (složenou z dalších komponent).
     * Pozn. Má vliv na vyhodnocení práv. Pro kompozitní komponenty se nevyhodnocují práva na podřízených komponentách.
     */
    isCompositeComponent?: boolean;
}
export class ICompositeComponent {
    private static _properties: (keyof ICompositeComponent)[] = [];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}

/**
 * Parametry konstruktoru tridy {@link GenericControl}.
 */
export type GenericControlParams = {
    readonly element: HTMLElement;
    /** Viz {@link IWithOriginalRightsDefinition.originalRightsDefinition}. */
    readonly originalRightsDefinition: IItemRightsCollection;
};

/**
 * Rozhraní pro komponenty, které pracují s nebo zobrazují hodnotu atributu aplikačního objektu
 * @includeToDoc
 */
export interface IAttrNameControl {
    /**
     * Název zobrazeného atributu
     */
    attrName: string;
}

export class IAttrNameControl {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            Reflect.has(instance, "attrName");
    }
}

/**
 * Interface pro komponentu spouštějící metodu
 */
export interface IMethodControl {
    readonly methodName: string;
}

export class IMethodControl {
    private static _properties: (keyof IMethodControl)[] = ["methodName"];
    private static _methods: string[] = [];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Rozhraní pro komponenty, které nesou nějakou vnitřní hodnotu (data)
 * @includeToDoc
 */
export interface IAttributeControl extends IAttrNameControl, ILoadableValue {
    valueChanged: Event<IEditableControl, any>;
    isVisuallyEncrypted: boolean;
    isValueLoaded: boolean;

    /**
     * Pro původní/nereactí komponenty může setter zadanou hodnotu modifikovat - např. oříznout řetězec delší jak maxLength.
     *
     * Pro React komponenty:
     * ----------------------
     * Setter nastaví `controlValue` na zadanou hodnotu.
     * - Tato hodnota může být i nevalidní (např. překračovat maxLength), přesto se 1:1 nastaví.
     * - Pokud je nová hodnota nevalidní a odlišná od iniciální hodnoty ({@link setInitialControlValue}), pak komponenta zčervená.
     */
    controlValue: any;

    /**
     * Pro původní/nereactí komponenty dělá to samé co `controlValue = value`.
     *
     * Pro React komponenty:
     * ----------------------
     * Nastaví `controlValue` na iniciální hodnotu - tj. na data načtená ze serveru.
     * - Tato hodnota může být i nevalidní, přesto se 1:1 nastaví.
     * - Iniciální hodnota má tu speciální vlastnost, že je považována vždy za "validní". V tom smyslu,
     * že i když je ve skutečnosti neplatná, tak u ní nechceme zobrazovat žádné chyby a chceme povolit její uložení.
     *
     * @param value Data načtená ze serveru.
     */
    setInitialControlValue(value: any): void;

    /**
     * Skutečně zobrazená hodnota v komponentě (i nevalidní).
     * Textová hodnota se získá pomocí toString metody.
     *
     * Pokud komponenta podporuje hromadnou editaci (rozhraní {@link IMultiEdit}) a hromadná
     * editace je aktivní, tak displayedValue vrací vždy null.
     */
    displayedValue: ITextBuilder;
}

export class IAttributeControl {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            Reflect.has(instance, "valueChanged") &&
            Reflect.has(instance, "isValueLoaded") &&
            Reflect.has(instance, "controlValue") &&
            Reflect.has(instance, "isVisuallyEncrypted") &&
            Reflect.has(instance, "displayedValue");
    }
}

/**
 * Rozhraní, která označuje komponentu, která při kliku zároveň nastavuje svou hodnotu. (např. CheckBox nebo RadioGroup).
 * Používá se na DF v situaci kdy DF není v editaci, klik na takovou komponentu má přepnout DF do editace, ktera
 * spouští vyhodnocení práv. Jakmile se DF vyhodnotí práva a přepne se do editace, znovu zavolá click na původní
 * komponentě, která nastaví svůj stav.
 */
export interface ISetValueAfterClickComponent {
    /** Komponenta na která inicializovala click událost */
    readonly clickInitializator: HTMLElement;
}
export class ISetValueAfterClickComponent {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "clickInitializator");
    }
}
/**
 * Rozhraní komponenty, která umožňuje omezit délku zadaného textu.
 * @includeToDoc
 */
export interface IMaxLength {
    /**Vrací maximální počet znaků podle nastavení z definice nebo databázového typu.*/
    maxLength: number;
}

export class IMaxLength {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "maxLength");
    }
}

/**
 * Interface, ktery urci pri registraci komponent, zda se ma volat registerFormUI, tzn chce si registrovat nejaky polozky na toolbar
 * @includeToDoc
 */
export interface IRegisterFormUIControl {
    registerControlFormUI(ownerForm: IForm): void;
}

export class IRegisterFormUIControl {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && typeof instance.registerControlFormUI === "function";
    }
}

/**
 * Interface, na zaklade, ktereho se predava informace o filtrovacim poli
 */
export interface IFilterInfo {
    filterPanel: FilterPanel;
    formName: string;
}

export class IFilterInfo {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null &&
            Reflect.has(instance, "filterPanel") &&
            Reflect.has(instance, "formName");
    }
}

/**
 * Rozhraní určující, že komponenta má dostávat i aktuálně vybraný aplikační objekt
 * @includeToDoc
 */
export interface IRequiresActualObject {
    actualObject: OWObject;
}

export class IRequiresActualObject {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "actualObject" as keyof IRequiresActualObject);
    }
}

/**
 * Rozhrání určující, že komponenta má mít přiřazený className.
 */
export interface IRequiresClassName {
    className: string;
}

export class IRequiresClassName {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "className" as keyof IRequiresClassName);
    }
}

/**
 * Rozhraní komponent, které mají stav povinnosti
 * @includeToDoc
 * @generateInstanceClass
 */
export interface IRequirableControl {
    required: boolean;
}

export class IRequirableControl {
    private static _properties: (keyof IRequirableControl)[] = ["required"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}

/**
 * Rozhraní komponent, které vyžadují kontrolu vyplnění u povinných, doporučených a odložených polí.
 * @includeToDoc
 */
export interface IRequirableFilledControl {
    recommended: boolean;
    postponedRequired: boolean;

    isFilledRequiredControl(): boolean;
    isFilledRecommendedControl(): boolean;
    isFilledPostponedControl(): boolean;
}

export class IRequirableFilledControl {
    private static _properties: (keyof IRequirableFilledControl)[] = ["recommended", "postponedRequired"];
    private static _methods: (keyof IRequirableFilledControl)[] = ["isFilledRequiredControl", "isFilledRecommendedControl", "isFilledPostponedControl"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

export interface IOpenAsEnabledControl extends IGenericControl {
    openAsEnabled: boolean;
}

export class IOpenAsEnabledControl {
    private static _properties = ["openAsEnabled"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IGenericControl;
    }
}

export interface ICloseFormControl extends IGenericControl {
    closeForm: boolean;
}

export class ICloseFormControl {
    private static _properties = ["closeForm"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IGenericControl;
    }
}

export interface IConfirmIfControl extends IGenericControl {
    confirmIf: boolean;
}

export class IConfirmIfControl {
    private static _properties = ["confirmIf"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IGenericControl;
    }
}

export interface IEncryptionControl extends IGenericControl {
    useDecryption: boolean;
    useEncryption: boolean;
}

export class IEncryptionControl {
    private static _properties = ["useDecryption", "useEncryption"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IGenericControl;
    }
}

export interface IDigitalSignControl extends IGenericControl {
    useDigitalSign: boolean;
}

export class IDigitalSignControl {
    private static _properties = ["useDigitalSign"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IGenericControl;
    }
}

export interface IValidateIfControl extends IGenericControl {
    validateIf: boolean;
}

export class IValidateIfControl {
    private static _properties = ["validateIf"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IGenericControl;
    }
}

/**
 * Interface pro komponenty které mohou být statické
 */
export interface IStaticControl {
    isStatic: boolean | null;
}

export class IStaticControl {
    private static _properties = ["isStatic"];
    private static _methods: string[] = [];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Interface, který umožňuje zamezit vyhodnocení práv na potomcích
 */
export interface IStopRightsEvaluatingOnChildren {
    /**
     * Zda již nevyhodnocovat práva na potomcích
     */
    stopRightsEvaluatingOnChildren?: boolean;
}

export class IStopRightsEvaluatingOnChildren {
    private static _properties = ["stopRightsEvaluatingOnChildren"];
    private static _methods: string[] = [];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Interface, který umožňuje zamezit vyhodnocení práv na komponentě
 */
export interface ICanIgnoreRights {
    /**
     * Zda se na komponentě nemají vyhodnocovat práva
     */
    ignoreRights?: boolean;
}

export class ICanIgnoreRights {
    private static _properties = ["ignoreRights"];
    private static _methods: string[] = [];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/** Interface pro komponenty, které o sobě poskytují informace */
export interface IInfoProvidingControl {
    /** Vrací objekt, který obsahuje informace o prvku */
    getControlInfo(childInfo: Partial<IControlInfoObject> | null, clickedControl: IGenericControl | null): Promise<Partial<IControlInfoObject>>;
}

export class IInfoProvidingControl {
    private static _methods = ["getControlInfo"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Rozhraní pro akce, které mají možnost být spouštěné nehledě na právo editace
 */
export interface IRunWithoutEdit {
    /**
     * Příznak, zda má být možné akci spustit bez ohledu na právo editace
     */
    runWithoutEdit: boolean;
}

export class IRunWithoutEdit {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && Reflect.has(instance, "runWithoutEdit");
    }
}

/**
 * Parametry konstruktoru tridy {@link Control}.
 * Používá se i pro konkrétní controls, které nemají extra parametry.
 */
export type ControlParams = GenericControlParams;

/**
 * Parametry konstruktoru pro controls, které navíc přijímají logger.
 */
export type ControlWithLoggerParams = ControlParams & {
    readonly logger: ISectionLogger;
};

/**
 * Parametry konstruktoru tridy {@link FormControl}.
 */
export type FormControlParams = ControlParams & {
    readonly ownerForm: IForm;
};

/**
 * Parametry konstruktoru pro form controllery, které navíc přijímají logger.
 */
export type FormControlWithLoggerParams = FormControlParams & {
    readonly logger: ISectionLogger;
};

/**
 * Rozhraní pro komponenty, které podporují hromadnou editaci.
 * @includeToDoc
 */
export interface IMultiEdit {
    /** Příznak značící, jestli je hromadná editace právě aktivní */
    multiEditActive: boolean;
    /** Příznak, zda proběhla interakce s fake komponentou v rámci jednoho cyklu hromadné editace. */
    readonly touched?: boolean;
}

export class IMultiEdit {
    private static _properties: (keyof IMultiEdit)[] = ["multiEditActive", "touched"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}

/**
 * Rozhraní pro komponenty podporující programové zahájení editace.
 * Využívá se při hromadné editaci pro zahájení editace po přepnutí z fake komponenty.
 */
export interface IStartEdit {

    /**
     * Zahájí editaci.
     */
    startEdit(): void;
}

export class IStartEdit {
    private static _methods: (keyof IStartEdit)[] = ["startEdit"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/** Interface pro komponenty, které mohou číst verzovaná data */
export interface IVersioned {
    /** Příznak, zda se mají číst verzovaná data */
    readVersioned: boolean;
}

export class IVersioned {
    private static _properties: (keyof IVersioned)[] = ["readVersioned"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}

/**
 * Rozhraní, které umožňuje komponentě předat kontext framu.
 */
export interface IHasFrameContext {
    /** Kontext framu */
    frameContext: FrameContext | undefined;
}
export class IHasFrameContext {
    private static _properties: (keyof IHasFrameContext)[] = ["frameContext"];
    private static _methods: (keyof IHasFrameContext)[] = [];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && this._properties.every((value) => Reflect.has(instance, value)) &&
            this._methods.every(value => typeof instance[value] === "function");
    }
}

/**
 * Rozhraní pro komponenty, které umožňují editaci vnitřní hodnoty. Rozhraní zároveň rozšiřuje následující rozhraní:
 * - {@link IAttributeControl}
 * - {@link IEnable}
 * - {@link IValidatable}
 * - {@link IRequirableControl}
 * - {@link IRequirableFilledControl}
 * - {@link IInteractive}
 * - {@link IEnabledVisualControl}
 * - {@link IInfoProvidingControl}
 * - {@link IStartEdit}
 * @includeToDoc
 */
export interface IEditableControl extends IControl, IAttributeControl, IEnable, IValidatable, IRequirableControl, IRequirableFilledControl, IInteractive, IEnabledVisualControl, IInfoProvidingControl, IStartEdit {
    isEditingChanged: Event<IEditableControl, any>;
    enabledChanged: Event<IEditableControl, any>;
    clicked: Event<IEditableControl, MouseEvent>;
    getFocus: Event<IEditableControl, FocusEvent>;
    lostFocus: Event<IEditableControl, FocusEvent>;
    hover: Event<IEditableControl, MouseEvent>;
    mouseEnter: Event<IEditableControl, MouseEvent>;
    mouseLeave: Event<IEditableControl, MouseEvent>;

    dataType: OWDataType | string;
    isEditing: boolean;
    innerElement: HTMLElement;

    focus(): void;
}

export class IEditableControl {
    private static _properties: string[] = ["isEditingChanged", "enabledChanged", "clicked", "getFocus", "lostFocus", "hover", "mouseEnter", "mouseLeave", "dataType", "isEditing", "innerElement"];
    private static _methods = ["focus"];
    private static _interfaces = [IControl, IAttributeControl, IEnable, IValidatable, IRequirableControl, IRequirableFilledControl, IInteractive, IEnabledVisualControl, IInfoProvidingControl, IStartEdit];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && this._properties.every((value) => Reflect.has(instance, value)) &&
            this._methods.every(value => typeof instance[value] === "function") &&
            this._interfaces.every((_interface) => instance instanceof _interface);
    }
}

/**
 * Rozhraní, které umožňuje komponentě předat hodnotu kurzoru myši.
 */
export interface ICursorValueAdjustable {
    cursor: Cursor;
}

export class ICursorValueAdjustable {
    private static _properties: (keyof ICursorValueAdjustable)[] = ["cursor"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && this._properties.every((value) => Reflect.has(instance, value));
    }
}

/**
 * Rozhraní, které umožňuje kontrolu předat informaci o tom, zda se mají validovat doporučená pole.
 */
export interface IValidateRecommendedControl {
    readonly validateRecommended: boolean;
}

export class IValidateRecommendedControl {
    private static _properties: (keyof IValidateRecommendedControl)[] = ["validateRecommended"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && this._properties.every((value) => Reflect.has(instance, value));
    }
}

/**
 * Rozhraní, které umožňuje používat kompozitní vazby
 */
export interface ICompositesControl {
    composites: string;
}

export class ICompositesControl {
    private static _properties: (keyof ICompositesControl)[] = ["composites"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && this._properties.every((value) => Reflect.has(instance, value));
    }
}
